import { useMutation, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import PRODUCT from 'api/fragments/PRODUCT';
import PRODUCT_VARIANT from 'api/fragments/PRODUCT_VARIANT';
import ADD_PRODUCT from 'api/mutations/ADD_PRODUCT';
import ADD_PRODUCT_PICTURES from 'api/mutations/ADD_PRODUCT_PICTURES';
import ARCHIVE_PRODUCT from 'api/mutations/ARCHIVE_PRODUCT';
import REMOVE_PRODUCT_PICTURE from 'api/mutations/REMOVE_PRODUCT_PICTURE';
import UPDATE_PRODUCT from 'api/mutations/UPDATE_PRODUCT';
import GET_CURRENT_TENANT from 'api/queries/GET_CURRENT_TENANT';
import GET_PRODUCT from 'api/queries/GET_PRODUCT';
import GET_PRODUCT_VARIANTS from 'api/queries/GET_PRODUCT_VARIANTS';
import { addNode, removeNode } from 'api/resolvers/resolvers';
import { assignedProductErrorHandler, registerAssignedProductErrorMatcher, } from 'errors/assignedProductError';
import { withAttachments } from 'utils/graphql';
import { dataValidationErrorHandler, registerDataValidationErrorMatcher, } from '../../errors/dataValidationError';
export default function useProductEditorView() {
    const dispatch = useDispatch();
    const { data: tenantData } = useQuery(GET_CURRENT_TENANT);
    const [addProduct, { loading: addProductLoading }] = useMutation(ADD_PRODUCT);
    const [addProductPictures] = useMutation(ADD_PRODUCT_PICTURES);
    const [removeProductPicture] = useMutation(REMOVE_PRODUCT_PICTURE);
    const [updateProduct, { loading: updateProductLoading }] = useMutation(UPDATE_PRODUCT);
    const [archiveProduct] = useMutation(ARCHIVE_PRODUCT);
    const handleProductAdd = (product) => {
        var _a, _b;
        return withAttachments(addProduct)({
            variables: Object.assign({ tenantId: (_b = (_a = tenantData === null || tenantData === void 0 ? void 0 : tenantData.me) === null || _a === void 0 ? void 0 : _a.currentTenant) === null || _b === void 0 ? void 0 : _b.id }, product),
            update(cache, newProduct) {
                var _a, _b;
                cache.modify({
                    id: cache.identify({ id: (_b = (_a = tenantData === null || tenantData === void 0 ? void 0 : tenantData.me) === null || _a === void 0 ? void 0 : _a.currentTenant) === null || _b === void 0 ? void 0 : _b.id, __typename: 'TenantNode' }),
                    fields: {
                        products(val) {
                            var _a, _b;
                            return addNode(val, (_b = (_a = newProduct === null || newProduct === void 0 ? void 0 : newProduct.data) === null || _a === void 0 ? void 0 : _a.addProduct) === null || _b === void 0 ? void 0 : _b.product, PRODUCT, cache, 'ProductNodeEdge');
                        },
                    },
                });
            },
        }).catch(dataValidationErrorHandler(dispatch));
    };
    const handleProductPicturesAdd = (productId, files) => {
        withAttachments(addProductPictures)({ variables: { productId, files } });
    };
    const handleProductPictureRemove = (productPictureId) => {
        removeProductPicture({ variables: { productPictureId } });
    };
    const handleProductUpdate = (product) => updateProduct({
        variables: product,
        update(cache) {
            Object.entries(cache.extract()).forEach((entry) => {
                // eslint-disable-next-line no-underscore-dangle
                if (entry[1].__typename === 'OrderNode') {
                    cache.evict({
                        id: entry[0],
                        fieldName: 'componentVariantsBySuppliers',
                    });
                }
            });
            cache.gc();
        },
        refetchQueries: [
            {
                query: GET_PRODUCT,
                variables: {
                    productId: product === null || product === void 0 ? void 0 : product.productId,
                },
            },
        ],
    }).catch(dataValidationErrorHandler(dispatch));
    registerDataValidationErrorMatcher();
    const handleProductArchive = (productId) => archiveProduct({
        variables: { productId },
        update(cache, archivedProduct) {
            var _a, _b;
            cache.modify({
                id: cache.identify({ id: productId, __typename: 'ProductNode' }),
                fields: {
                    variants(val) {
                        var _a, _b, _c, _d;
                        if (((_a = val === null || val === void 0 ? void 0 : val.args) === null || _a === void 0 ? void 0 : _a.status) === 'ARCHIVED') {
                            const variants = cache.readQuery({
                                query: GET_PRODUCT_VARIANTS,
                                variables: { productId, status: 'ACTIVE' },
                            });
                            const variantsEdges = ((_d = (_c = (_b = variants === null || variants === void 0 ? void 0 : variants.node) === null || _b === void 0 ? void 0 : _b.variants) === null || _c === void 0 ? void 0 : _c.edges) === null || _d === void 0 ? void 0 : _d.map(({ __typename, node }) => ({
                                __typename,
                                node: cache.writeFragment({
                                    data: Object.assign(Object.assign({}, node), { status: 'ARCHIVED' }),
                                    fragment: PRODUCT_VARIANT,
                                }),
                            }))) || [];
                            return Object.assign(Object.assign({}, val), { edges: [...val.edges, ...variantsEdges] });
                        }
                        return Object.assign(Object.assign({}, val), { edges: [] });
                    },
                },
            });
            cache.modify({
                id: cache.identify({ id: (_b = (_a = tenantData === null || tenantData === void 0 ? void 0 : tenantData.me) === null || _a === void 0 ? void 0 : _a.currentTenant) === null || _b === void 0 ? void 0 : _b.id, __typename: 'TenantNode' }),
                fields: {
                    products(val, { readField }) {
                        var _a, _b, _c, _d;
                        if (((_a = val === null || val === void 0 ? void 0 : val.args) === null || _a === void 0 ? void 0 : _a.status) === 'ACTIVE') {
                            return removeNode(val, readField, productId);
                        }
                        if (((_b = val === null || val === void 0 ? void 0 : val.args) === null || _b === void 0 ? void 0 : _b.status) === 'ARCHIVED') {
                            return addNode(val, (_d = (_c = archivedProduct === null || archivedProduct === void 0 ? void 0 : archivedProduct.data) === null || _c === void 0 ? void 0 : _c.archiveProduct) === null || _d === void 0 ? void 0 : _d.product, PRODUCT, cache, 'ProductNodeEdge');
                        }
                        return val;
                    },
                },
            });
        },
    }).catch(assignedProductErrorHandler('product_assigned_cannot_archive_product'));
    registerAssignedProductErrorMatcher();
    return {
        onProductAdd: handleProductAdd,
        onProductPicturesAdd: handleProductPicturesAdd,
        onProductPictureRemove: handleProductPictureRemove,
        onProductUpdate: handleProductUpdate,
        onProductArchive: handleProductArchive,
        addProductLoading,
        updateProductLoading,
    };
}
